import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { TransitsApi } from '../apis/TransitsApi';
import {
  formatDateTime,
  translateTransitsApiResponse
} from '../common/tranists';
import { useWindowFocus } from '../hooks/useWindowFocus';
import { DialogStateContext } from './DialogStateContextProvider';
import { MasterContext } from './MasterContextProvider';

export type TransitsType = {
  icons_overview: Array<IconOverviewType>; //全アイコンのマップ上表示に関する情報
  icons_overview_low_floor: Array<IconOverviewType>;
  icons_zoom: Array<IconZoomType>;
  icons_zoom_low_floor: Array<IconZoomType>;
  dialog_places: DialogPlacesType; //placeごとにまとまった車両情報ポップアップ用の情報
  dialog_places_low_floor: DialogPlacesType;
};

export type IconOverviewType = {
  x: number;
  y: number;
  train_no: number;
  color: string;
  arrow: number;
  logo: boolean;
  onStation: boolean;
};

export type IconZoomType = {
  x: number;
  y: number;
  train_no: number;
  color: string;
  arrow: number;
  logo: boolean;
  place_id: number;
  index_in_place: number;
};

export type DialogPlacesType = {
  [key in string]: DialogTrainsInfoType;
};

export type DialogTrainsInfoType = {
  trains: Array<DialogTrainInfoType>;
};

export type DialogTrainInfoType = {
  self_station_no: string;
  station_name_former: string;
  station_name_latter: string;
  station_name_former_en: string;
  station_name_latter_en: string;
  train_no: number;
  course: number;
  low_floor_flag: boolean;
  destination_name: string;
  destination_name_en: string;
  schedules: Array<ScheduleType>;
};

type ScheduleType = {
  no: string;
  ja: string;
  en: string;
  time: string | null;
};

const initialState: TransitsType = {
  icons_overview: [],
  icons_overview_low_floor: [],
  icons_zoom: [],
  icons_zoom_low_floor: [],
  dialog_places: {},
  dialog_places_low_floor: {}
};

type TransitsContextType = {
  transits: TransitsType;
  setTransits: React.Dispatch<React.SetStateAction<TransitsType>>;
  failedGetTransits: boolean; //取得処理失敗フラグ
  updateTransits: () => void;
  lastUpdateTime: string; //最終更新時間
};

export const TransitsContext = createContext({} as TransitsContextType);

const API_INTERVAL = 60000; //取得タイマーの周期
const WAITING_TIME = 2000; //リクエスト実行の最小間隔

type Props = {
  children: React.ReactNode;
};

// 位置情報を取得～記憶するContext
export const TransitsContextProvider: React.FC<Props> = (props) => {
  const { dialogState } = useContext(DialogStateContext);
  const { master } = useContext(MasterContext);
  const [transits, setTransits] = useState<TransitsType>(initialState);
  const [failedGetTransits, setFailedGetTransits] = useState(false); //取得失敗フラグ
  const [lastUpdateTime, setLastUpdateTime] = useState(''); //最終更新時間
  const [updateTrigger, setUpdateTrigger] = useState(true); //更新時にタイマーをリセットするためのstate
  const [trigger, setTrigger] = useState(true); //取得処理を発火させるためのstate

  const focused = useRef(true); //画面がアクティブか
  focused.current = useWindowFocus();
  const firstRef = useRef(true); //初回取得実行フラグ
  const getTransitsReady = useRef(true); //APIデータ更新をしてよい状態か
  const timeRef = useRef(Date.now()); //最終リクエスト時刻

  // 一定間隔で取得処理をトリガー
  useEffect(() => {
    const intervalId = setInterval(() => {
      master.stations.length > 0 && setTrigger((pre) => !pre);
    }, API_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [master.stations.length, updateTrigger]);

  // 更新ボタン押下時はタイマーリセットして、取得処理をトリガー
  const updateTransits = useCallback(() => {
    setUpdateTrigger((pre) => !pre);
    setTrigger((pre) => !pre);
  }, []);

  // 車両情報ポップアップ表示中は更新しない
  useEffect(() => {
    getTransitsReady.current =
      dialogState.isOpen === true && dialogState.kind === 'placeInfo'
        ? false
        : true;
  }, [dialogState.isOpen, dialogState.kind]);

  useEffect(() => {
    const getApi = async () => {
      if (!getTransitsReady.current) return;
      const res = await TransitsApi();
      // console.log('🚀  位置情報：APIデータ', res?.datas);

      if (res === null) {
        setTransits(initialState);
        setFailedGetTransits(true);
        setLastUpdateTime('');
        // console.log('🚀  位置情報（エラー）：加工後', initialState);
      } else {
        setTransits(translateTransitsApiResponse(res, initialState));
        setFailedGetTransits(false);
        setLastUpdateTime(formatDateTime(new Date(res.time)));
        // console.log(
        //   '🚀  位置情報：加工後',
        //   translateTransitsApiResponse(res, initialState)
        // );
      }
    };

    const now = Date.now();
    const diff = now - timeRef.current;
    // 初回または、前回から一定時間以上経過しているときのみリクエスト
    if (
      (getTransitsReady.current && focused.current && diff > WAITING_TIME) ||
      firstRef.current
    ) {
      timeRef.current = now;
      firstRef.current = false;
      getApi();
    }
  }, [trigger]);

  return (
    <TransitsContext.Provider
      value={{
        transits,
        setTransits,
        failedGetTransits,
        updateTransits,
        lastUpdateTime
      }}
    >
      {props.children}
    </TransitsContext.Provider>
  );
};
